// src/services/PerformanceMonitor.ts
/**
 * 📊 Performance Monitor Service
 *
 * Tracks and analyzes application performance metrics:
 * - Widget rendering times
 * - Metrics calculation duration
 * - Cache hit/miss ratios
 * - Component mount/unmount cycles
 * - Memory usage patterns
 *
 * @version 1.0.0
 */

export interface PerformanceEntry {
  id: string;
  category: 'widget' | 'metrics' | 'render' | 'cache' | 'data';
  operation: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
  success: boolean;
  error?: string;
}

export interface PerformanceStats {
  category: string;
  operation: string;
  count: number;
  totalDuration: number;
  avgDuration: number;
  minDuration: number;
  maxDuration: number;
  successRate: number;
  lastRun: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  hitRate: number;
  totalRequests: number;
  avgRetrievalTime: number;
}

/**
 * Performance Monitor Singleton
 */
class PerformanceMonitorClass {
  private entries: PerformanceEntry[] = [];
  private maxEntries = 1000; // Keep last 1000 entries
  private cacheHits = 0;
  private cacheMisses = 0;
  private enabled = true; // Can be disabled in production

  /**
   * Start tracking an operation
   */
  startOperation(
    category: PerformanceEntry['category'],
    operation: string,
    metadata?: Record<string, any>
  ): string {
    if (!this.enabled) return '';

    const id = `${category}_${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const entry: PerformanceEntry = {
      id,
      category,
      operation,
      startTime: performance.now(),
      success: true,
      metadata
    };

    this.entries.push(entry);
    this.cleanupOldEntries();

    return id;
  }

  /**
   * End tracking an operation
   */
  endOperation(id: string, success = true, error?: string): void {
    if (!this.enabled || !id) return;

    const entry = this.entries.find(e => e.id === id);
    if (!entry) {
      console.warn(`⚠️ Performance entry not found: ${id}`);
      return;
    }

    entry.endTime = performance.now();
    entry.duration = entry.endTime - entry.startTime;
    entry.success = success;
    entry.error = error;

    // Log slow operations (> 1000ms)
    if (entry.duration > 1000) {
      console.warn(`🐌 Slow operation detected: ${entry.category}.${entry.operation} took ${entry.duration.toFixed(2)}ms`);
    }
  }

  /**
   * Track a cache hit
   */
  recordCacheHit(): void {
    if (!this.enabled) return;
    this.cacheHits++;
  }

  /**
   * Track a cache miss
   */
  recordCacheMiss(): void {
    if (!this.enabled) return;
    this.cacheMisses++;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): CacheStats {
    const totalRequests = this.cacheHits + this.cacheMisses;
    const hitRate = totalRequests > 0 ? this.cacheHits / totalRequests : 0;

    // Calculate average retrieval time from cache-related entries
    const cacheEntries = this.entries.filter(e => e.category === 'cache' && e.duration);
    const avgRetrievalTime = cacheEntries.length > 0
      ? cacheEntries.reduce((sum, e) => sum + (e.duration || 0), 0) / cacheEntries.length
      : 0;

    return {
      hits: this.cacheHits,
      misses: this.cacheMisses,
      hitRate,
      totalRequests,
      avgRetrievalTime
    };
  }

  /**
   * Get performance statistics for a specific operation
   */
  getOperationStats(category: string, operation: string): PerformanceStats | null {
    const relevantEntries = this.entries.filter(
      e => e.category === category && e.operation === operation && e.duration !== undefined
    );

    if (relevantEntries.length === 0) return null;

    const durations = relevantEntries.map(e => e.duration!);
    const totalDuration = durations.reduce((sum, d) => sum + d, 0);
    const successCount = relevantEntries.filter(e => e.success).length;

    return {
      category,
      operation,
      count: relevantEntries.length,
      totalDuration,
      avgDuration: totalDuration / relevantEntries.length,
      minDuration: Math.min(...durations),
      maxDuration: Math.max(...durations),
      successRate: successCount / relevantEntries.length,
      lastRun: relevantEntries[relevantEntries.length - 1].endTime || 0
    };
  }

  /**
   * Get all performance statistics grouped by category
   */
  getAllStats(): PerformanceStats[] {
    const statsMap = new Map<string, PerformanceStats>();

    // Group by category + operation
    this.entries
      .filter(e => e.duration !== undefined)
      .forEach(entry => {
        const key = `${entry.category}:${entry.operation}`;

        if (!statsMap.has(key)) {
          const durations = this.entries
            .filter(e => e.category === entry.category && e.operation === entry.operation && e.duration)
            .map(e => e.duration!);

          const successCount = this.entries
            .filter(e => e.category === entry.category && e.operation === entry.operation && e.success)
            .length;

          statsMap.set(key, {
            category: entry.category,
            operation: entry.operation,
            count: durations.length,
            totalDuration: durations.reduce((sum, d) => sum + d, 0),
            avgDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
            minDuration: Math.min(...durations),
            maxDuration: Math.max(...durations),
            successRate: successCount / durations.length,
            lastRun: entry.endTime || 0
          });
        }
      });

    return Array.from(statsMap.values());
  }

  /**
   * Get performance summary
   */
  getSummary() {
    const allStats = this.getAllStats();
    const cacheStats = this.getCacheStats();

    // Group by category
    const byCategory = allStats.reduce((acc, stat) => {
      if (!acc[stat.category]) {
        acc[stat.category] = [];
      }
      acc[stat.category].push(stat);
      return acc;
    }, {} as Record<string, PerformanceStats[]>);

    // Find slowest operations
    const slowestOps = [...allStats]
      .sort((a, b) => b.avgDuration - a.avgDuration)
      .slice(0, 5);

    // Find operations with lowest success rate
    const problematicOps = [...allStats]
      .filter(s => s.successRate < 1)
      .sort((a, b) => a.successRate - b.successRate)
      .slice(0, 5);

    return {
      totalOperations: this.entries.length,
      completedOperations: this.entries.filter(e => e.duration !== undefined).length,
      categories: Object.keys(byCategory),
      cache: cacheStats,
      slowestOperations: slowestOps,
      problematicOperations: problematicOps,
      byCategory
    };
  }

  /**
   * Clear all performance data
   */
  clear(): void {
    this.entries = [];
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  /**
   * Export performance data for analysis
   */
  export(): { entries: PerformanceEntry[]; summary: any } {
    return {
      entries: [...this.entries],
      summary: this.getSummary()
    };
  }

  /**
   * Enable/disable performance monitoring
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      console.log('📊 Performance monitoring disabled');
    } else {
      console.log('📊 Performance monitoring enabled');
    }
  }

  /**
   * Check if monitoring is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Cleanup old entries to prevent memory leaks
   */
  private cleanupOldEntries(): void {
    if (this.entries.length > this.maxEntries) {
      const removeCount = this.entries.length - this.maxEntries;
      this.entries.splice(0, removeCount);
    }
  }

  /**
   * Log performance report to console
   */
  logReport(): void {
    const summary = this.getSummary();

    console.group('📊 Performance Report');
    console.log(`Total Operations: ${summary.totalOperations}`);
    console.log(`Completed: ${summary.completedOperations}`);
    console.log(`Categories: ${summary.categories.join(', ')}`);

    console.group('💾 Cache Performance');
    console.log(`Hit Rate: ${(summary.cache.hitRate * 100).toFixed(2)}%`);
    console.log(`Hits: ${summary.cache.hits} | Misses: ${summary.cache.misses}`);
    console.log(`Avg Retrieval: ${summary.cache.avgRetrievalTime.toFixed(2)}ms`);
    console.groupEnd();

    if (summary.slowestOperations.length > 0) {
      console.group('🐌 Slowest Operations');
      summary.slowestOperations.forEach(op => {
        console.log(`${op.category}.${op.operation}: ${op.avgDuration.toFixed(2)}ms (${op.count} calls)`);
      });
      console.groupEnd();
    }

    if (summary.problematicOperations.length > 0) {
      console.group('⚠️ Problematic Operations');
      summary.problematicOperations.forEach(op => {
        console.log(
          `${op.category}.${op.operation}: ${(op.successRate * 100).toFixed(1)}% success (${op.count} calls)`
        );
      });
      console.groupEnd();
    }

    console.groupEnd();
  }
}

// Export singleton instance
export const PerformanceMonitor = new PerformanceMonitorClass();

// Development helper: expose to window for debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).performanceMonitor = PerformanceMonitor;
}
